import BottomSheet from '@components/BottomSheet'
import Prompt from '@components/Prompt'
import useStatusPill from '@hooks/useStatusPill'
import { TokenCard } from '@modules/Swap/ExchangeView'
import { WaterNeuron } from '@plug-core/clients/WaterNeuron'
import { STAKE_COMMISSION } from '@plug-core/constants/business'
import useBalances from '@plug-core/hooks/useBalances'
import { useStaking } from '@plug-core/hooks/useStaking'
import { ICToken } from '@plug-core/interfaces/common'
import { WaterNeuronInfo } from '@plug-core/interfaces/staking'
import { Button, Heading, ListGroup, ListRow, Tabs, TabsList, TabsTrigger, Text } from '@ui'
import React, { PropsWithChildren, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { StakeButton } from './StakeButton'
import { StakeReview } from './StakeReview'
import { UnstakeButton } from './UnstakeButton'

const StakeListItem = ({
  attribute,
  children,
}: PropsWithChildren & {
  attribute: string
}) => (
  <ListRow
    primary={<span className="text-[14px] font-normal">{attribute}</span>}
    trailing={<span className="tabular text-[14px]">{children}</span>}
  />
)

export const WaterNeuronStaking = ({
  icpToken,
  nicpToken,
  wnInfo,
  waterNeuron,
}: {
  icpToken: ICToken
  nicpToken: ICToken
  wnInfo: WaterNeuronInfo
  waterNeuron: WaterNeuron
}) => {
  const { t } = useTranslation()
  const { tokensToBalances } = useBalances()

  const {
    stakeMode,
    setStakeMode,
    token,
    amount,
    setAmount,
    numericAmount,
    exchangeRate,
    setReviewData,
    isLoading,
    commission,
    success,
    setSuccess,
    reviewData,
    onSubmit,
    nicpReceiveAmount,
    wtnReward,
  } = useStaking({ icpToken, nicpToken, wnInfo, waterNeuron })

  const { showStatus } = useStatusPill()

  useEffect(() => {
    if (success) showStatus(t(stakeMode === 'stake' ? 'stake.staked' : 'stake.unstaked'))
  }, [success])

  return (
    <>
      <Tabs
        value={stakeMode === 'stake' ? '0' : '1'}
        onValueChange={(index) => setStakeMode(index === '0' ? 'stake' : 'unstake')}
        className="mb-4"
      >
        <TabsList className="self-stretch">
          <TabsTrigger value="0" className="flex-1">
            {t('stake.stakeIcp')}
          </TabsTrigger>
          <TabsTrigger value="1" className="flex-1">
            {t('stake.unstakeNicp')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <TokenCard
        token={token}
        amount={amount}
        setAmount={setAmount}
        direction="from"
        error={undefined}
        showBalance
        disableTokenPicker
        tokensToBalances={tokensToBalances}
      />

      <ListGroup className="mt-2.5">
        {stakeMode === 'stake' && (
          <StakeListItem attribute={t('stake.receiveAmount')}>{nicpReceiveAmount}</StakeListItem>
        )}

        <StakeListItem attribute={t('stake.exchangeRate')}>{exchangeRate}</StakeListItem>

        {stakeMode === 'stake' && (
          <StakeListItem attribute={t('stake.futureReward')}>{wtnReward}</StakeListItem>
        )}
      </ListGroup>

      <div className="mt-2.5 flex flex-col">
        {stakeMode === 'stake' && (
          <StakeButton
            review={setReviewData}
            waterNeuron={waterNeuron}
            isLoading={isLoading}
            amount={numericAmount}
            commission={commission}
            wnInfo={wnInfo}
            icpToken={icpToken}
            nicpToken={nicpToken}
          />
        )}

        {stakeMode === 'unstake' && (
          <UnstakeButton
            review={setReviewData}
            waterNeuron={waterNeuron}
            isLoading={isLoading}
            amount={numericAmount}
            commission={commission}
            wnInfo={wnInfo}
            icpToken={icpToken}
            nicpToken={nicpToken}
          />
        )}
      </div>

      <BottomSheet
        visible={!!reviewData}
        onBackdropClick={() => !isLoading && setReviewData(undefined)}
      >
        <StakeReview
          data={reviewData!}
          amount={amount}
          fee={STAKE_COMMISSION}
          mode={stakeMode}
          isLoading={isLoading}
          icpToken={icpToken}
          nicpToken={nicpToken}
          submit={onSubmit}
          close={() => setReviewData(undefined)}
        />
      </BottomSheet>

      <Prompt show={success === false} close={() => setSuccess(null)}>
        <div className="flex flex-col items-center gap-2">
          <Heading level={4} align="center">
            {t('stake.error.title')}
          </Heading>
          <Text align="center">{t('stake.error.description')}</Text>
        </div>

        <Button onClick={() => setSuccess(null)} variant="secondary">
          {t('common.understood')}
        </Button>
      </Prompt>
    </>
  )
}
